import { createContext, useEffect, useState } from 'react';
import { loadKpis, loadPricing } from '../api';

const PRICING_REFRESH_MS = 30 * 60 * 1000;
const KPI_REFRESH_MS = 5 * 60 * 1000;
const RETRY_MS = 2 * 60 * 1000;

// Loads one server-cached document for a property and keeps it fresh. The server does the real throttling; this
// only decides how often a screen asks. `state` is 'loading', 'ready', or 'error' (with the last good data kept).
function useServerDocument(load, propertyId, refreshMs) {
  const [result, setResult] = useState({ data: null, state: 'loading', error: null });
  useEffect(() => {
    if (!propertyId) return undefined;
    let current = true;
    let timer;
    async function refresh() {
      try {
        const data = await load(propertyId);
        if (!current) return;
        setResult({ data, state: data ? 'ready' : 'unavailable', error: null });
        timer = setTimeout(refresh, refreshMs);
      } catch (error) {
        if (!current) return;
        setResult((previous) => ({ data: previous.data, state: 'error', error: error.message }));
        timer = setTimeout(refresh, RETRY_MS);
      }
    }
    setResult({ data: null, state: 'loading', error: null });
    refresh();
    return () => { current = false; clearTimeout(timer); };
  }, [load, propertyId, refreshMs]);
  return result;
}

export const usePricing = (propertyId) => useServerDocument(loadPricing, propertyId, PRICING_REFRESH_MS);
export const useKpis = (propertyId) => useServerDocument(loadKpis, propertyId, KPI_REFRESH_MS);

// One pricing document per screen, shared by the pricing pages and the calendar's demand tint.
export const PricingContext = createContext({ data: null, state: 'loading', error: null });
